import type { ComponentProps, ReactNode } from 'react';
import { Link as RouterLink, type LinkProps as RouterLinkProps } from 'react-router';
import { ExternalLink } from 'lucide-react';
import { cn } from '../../lib/cn';
import { buttonClasses, type ButtonSize, type ButtonVariant } from './buttonStyles';

const linkClass =
  'font-bold text-primary underline underline-offset-4 decoration-2 hover:text-primary-hover hover:decoration-[3px]';

export type LinkProps = RouterLinkProps & { muted?: boolean };

/** In-app link (React Router). */
export function Link({ className, muted, ...rest }: LinkProps) {
  return <RouterLink className={cn(linkClass, muted && 'font-normal text-muted', className)} {...rest} />;
}

export interface ExternalAnchorProps extends ComponentProps<'a'> {
  href: string;
  children: ReactNode;
}

/** Link to another website: opens in a new tab and says so to screen readers. */
export function ExternalAnchor({ className, children, ...rest }: ExternalAnchorProps) {
  return (
    <a
      // Stays an inline element so it wraps inside sentences and gets the global 44px link target (index.css).
      className={cn(linkClass, className)}
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
    >
      {children}
      <ExternalLink aria-hidden="true" className="ml-1 inline-block h-[0.85em] w-[0.85em] align-[-0.05em]" />
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

export type ButtonLinkProps = RouterLinkProps & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
};

/** A router link that looks like a button (use for navigation; use <Button> for actions). */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  fullWidth,
  leftIcon,
  rightIcon,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <RouterLink className={buttonClasses({ variant, size, fullWidth, className })} {...rest}>
      {leftIcon}
      <span>{children}</span>
      {rightIcon}
    </RouterLink>
  );
}
